import { Base, Component, Prop, Watch } from '@/vue-property-decorator'
import { Bind, Debounce } from '@/utils/Decorator'
import { CURRENT_LANGUAGE_CONFIG, rectIconMap } from '@/config/lang.config'
import { CanClickWhenForceBind } from '@/views/center/security/store'
import {
  Dropdown,
  Icon,
  Popover,
  Row
} from '@/plugins/ant-design-vue/tsx-support'
import { GlobalConst, GlobalEvents } from '@/context'
import { GlobalEventActions } from '@/context/events'
import { Language } from '@/i18n'
import { cloneDeep } from 'lodash'
import { createSvgSprite } from '@/utils/business-utils/assets'
import { useHomeStore } from '@/store/home'
import { useMainStore } from '@/store/index'
import AppTypes from '@/vue-types'
import BusinessUtils from '@/utils/business-utils'
import Skin from '@/controller/Skin'
import SwitchLangOptions from './options'
import style from './style.module.scss'

interface Props {
  getPopupContainer?: (triggerNode?: HTMLElement) => HTMLElement

  width?: number
  /**
   * 语言展开模式（下拉居中，右侧居上）
   */
  placement?:
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight'
    | 'rightTop'
    | 'bottom'
  /** 禁止选择语言 */
  disabled?: boolean
  /** 是否是在头部header 决定旋转角度90或180 */
  isHeaderSwitch?: boolean
  /**是否有边框 头部的才给边框 脚部和侧边栏的不要*/
  border?: boolean
  /** 后缀图标 */
  suffixArrow?: VueTsxSupport.JSX.Element | (() => VueTsxSupport.JSX.Element)
  /**前缀图标 */
  prefixArrow?: VueTsxSupport.JSX.Element | (() => VueTsxSupport.JSX.Element)
}
interface State {
  loading: boolean
  visible: boolean
}

@Component<SwitchLang>({
  name: 'SwitchLang'
})
export default class SwitchLang extends Base<State, Props> {
  @Prop(AppTypes.number)
  readonly width!: Props['width']
  @Prop(AppTypes.string.def('bottomLeft'))
  readonly placement!: Props['placement']
  @Prop({ required: false })
  getPopupContainer!: Props['getPopupContainer']
  @Prop(AppTypes.bool.def(false))
  readonly disabled!: Props['disabled']
  @Prop({ default: false })
  readonly isHeaderSwitch?: Props['isHeaderSwitch']
  @Prop({ default: false })
  readonly border?: Props['border']
  @Prop({ default: null })
  readonly suffixArrow?: Props['suffixArrow']
  @Prop({ default: null })
  readonly prefixArrow?: Props['prefixArrow']
  state = {
    loading: false,
    visible: false
  }

  private get h5AsideShow() {
    return useHomeStore().h5AsideShow
  }

  private get isWeb() {
    return useMainStore()?.isWeb
  }

  private get langList() {
    const appStore = useMainStore()
    return (
      cloneDeep(appStore?.siteInfos?.languageInfos || [])
        .sort((it) => (it.defaultLanguageTag ? -1 : 1))
        .filter((it) => CURRENT_LANGUAGE_CONFIG.includes(it.languageCode)) || []
    )
  }

  private get singleList() {
    const { isTheme34 } = Skin.currentTheme()
    return isTheme34 ? this.langList.length === 0 : this.langList.length <= 1
  }

  private renderCountryIcon(lang: Language) {
    return (
      <Icon.Tsx
        class={['switch-lang__icon']}
        style={{ display: 'flex', alignItems: 'center' }}
        component={{
          functional: true,
          render: () => {
            const icon = rectIconMap[lang]()
            const scale = 20 / icon?.width
            return <icon-sprite scale={scale} sprite={icon}></icon-sprite>
          }
        }}
      />
    )
  }

  private renderCurrentItem() {
    const { isWeb } = useMainStore()
    const { isGalaxyGold, isCrownGold } = Skin.currentTheme()
    const appStore = useMainStore()
    const lang = appStore.language
    const name =
      this.langList.find(
        (it) => it.languageCode === GlobalConst.ServiceLanguageMap[lang]
      )?.languageTranslateName || ''
    return (
      <Row.Tsx
        title={name}
        class={[
          style.item,
          CanClickWhenForceBind,
          this.state.visible ? 'langCurrentPopupOpen' : 'langCurrentPopupClose'
        ]}
        type={'flex'}
        align={'middle'}
      >
        {(isGalaxyGold || isCrownGold) && isWeb && this.renderCountryIcon(lang)}
        <span
          class={[GlobalConst.CssClassName.Lobby.Ellipsis, 'switch-lang__text']}
        >
          {name}
        </span>
      </Row.Tsx>
    )
  }

  private renderLangList() {
    return (
      <SwitchLangOptions
        class={this.state.visible ? 'langPopupOpen' : 'langPopupClose'}
        onSelected={() => {
          this.setState({ visible: false })
        }}
        onLoadState={(load = false) => {
          this.setState({ loading: load })
        }}
      />
    )
  }
  @Bind()
  @Debounce(50)
  public switchPopover() {
    this.setState({
      visible: !this.state.visible
    })
  }

  @Watch('h5AsideShow')
  private handleH5AsideShowChange() {
    if (!this.h5AsideShow) {
      this.setState({
        visible: false
      })
    }
  }

  private renderRightArrow(show = true) {
    return (
      <Icon.Tsx
        v-show={show}
        class={[style['right-icon'], 'switch-lang__arrow']}
        component={{
          functional: true,
          render: () => {
            if (this.state.loading) {
              return <Icon.Tsx type={'loading'} />
            }
            return this.suffixArrow ? (
              typeof this.suffixArrow == 'function' ? (
                this.suffixArrow()
              ) : (
                this.suffixArrow
              )
            ) : (
              <icon-sprite
                class={[style.arrow, 'arrow']}
                sprite={createSvgSprite('comm_icon_sort')}
                useSkeleton
                attrs={{
                  'data-visible': this.state.visible
                }}
              />
            )
          }
        }}
      />
    )
  }

  @Bind()
  closeDropdown(action: GlobalEventActions) {
    if (action.type === 'CLOSE_DROPDOWN') {
      //强制刷新
      this.setState({
        visible: false
      })
      this.$forceUpdate()
    }
  }

  created() {
    GlobalEvents.subscribe(this.closeDropdown)
  }

  // beforeDestroy() {
  //   this.setState({
  //     loading: false,
  //     visible: false
  //   })
  //   GlobalEvents.subscribe(this.closeDropdown)
  // }

  render() {
    const { isStake } = Skin.currentTheme()
    const { collapsed } = useHomeStore()
    // 如果只有一个语言选项 则不展示
    if (this.singleList) {
      return <div></div>
    }

    if (this.disabled) {
      return <div class={style.disabled}>{this.renderCurrentItem()}</div>
    }
    if (this.placement === 'bottom') {
      return (
        <span class={[style.languagePopver, style.languagePopverBottom]}>
          <span
            class={[
              style.prefixWarp,
              'prefixWarp',
              this.state.visible ? 'wrapActived' : ''
            ]}
          >
            {this.prefixArrow &&
              (typeof this.prefixArrow == 'function'
                ? this.prefixArrow()
                : this.prefixArrow)}

            <div
              class={[
                style['current-container'],
                'current-container',
                'lang-switch',
                this.state.visible
                  ? 'langCurrentTitlePopupOpen'
                  : 'langCurrentTitlePopupClose'
              ]}
              onClick={this.switchPopover}
            >
              <icon-sprite
                v-show={isStake && this.isWeb}
                class={style.globalIcon}
                sprite={createSvgSprite('icon_sy_zc_yy')}
                useSkeleton
              />
              {!(isStake && collapsed) && this.renderCurrentItem()}
              {this.renderRightArrow(!(isStake && collapsed))}
            </div>
          </span>
          <div v-show={this.state.visible}>
            <my-scroll
              height={
                this.langList.length > 6
                  ? BusinessUtils.px2rem('360px')
                  : '100%'
              }
              {...{
                directives: [
                  {
                    name: 'click-outside',
                    value: () => {
                      if (this.state.visible) this.switchPopover()
                    }
                  }
                ]
              }}
            >
              {this.renderLangList()}
            </my-scroll>
          </div>
        </span>
      )
    }
    if (this.placement === 'rightTop') {
      return (
        <Popover.Tsx
          class={[CanClickWhenForceBind]}
          trigger={['click']}
          visible={this.state.visible}
          placement="rightTop"
          getPopupContainer={this.getPopupContainer}
          overlayClassName={`${style.languagePopver} switch-languagePopver`}
        >
          <div
            class={[
              style['current-container'],
              'current-container',
              'lang-switch'
            ]}
            onClick={this.switchPopover}
            style={{ height: 'auto' }}
          >
            <icon-sprite
              v-show={isStake && this.isWeb}
              class={style.globalIcon}
              sprite={createSvgSprite('icon_sy_zc_yy')}
              useSkeleton
            />
            {!(isStake && collapsed) && this.renderCurrentItem()}
            {this.renderRightArrow(!(isStake && collapsed))}
          </div>

          {this.langList.length > 0 && (
            <template slot="content">
              <my-scroll
                height={
                  this.langList.length > 6
                    ? BusinessUtils.px2rem('360px')
                    : '100%'
                }
                {...{
                  directives: [
                    {
                      name: 'click-outside',
                      value: () => {
                        if (this.state.visible) this.switchPopover()
                      }
                    }
                  ]
                }}
              >
                {this.renderLangList()}
              </my-scroll>
            </template>
          )}
        </Popover.Tsx>
      )
    }

    return (
      <div class={[style['lang-switch-header'], 'lang-switch']}>
        <Dropdown.Tsx
          class={[CanClickWhenForceBind]}
          trigger={['click']}
          placement={this.placement}
          v-model={this.state.visible}
          overlayClassName={style.languagePopup}
          getPopupContainer={this?.getPopupContainer}
        >
          <template slot="overlay">
            <my-scroll direction="y">{this.renderLangList()}</my-scroll>
          </template>
          <div
            class={[
              style['current-container'],
              this.border ? style['border-box'] : '',
              'current-container'
            ]}
          >
            {this.renderCurrentItem()}
            {this.renderRightArrow()}
          </div>
        </Dropdown.Tsx>
      </div>
    )
  }
}
